// frontend/src/pages/Register.jsx
import React, { useState, useRef } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  useTheme,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link as MuiLink,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Visibility, VisibilityOff, CheckCircle, ErrorOutline } from "@mui/icons-material";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Turnstile } from "@marsidev/react-turnstile"; // 👈 NEW
import heroImg from "../assets/register.jpg";

const BLUE = {
  main: "#1976d2",
  dark: "#1565c0",
  light: "#bbdefb",
  gradient: "linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)"
};

const roles = ["employee", "project-manager", "hr", "hr-manager", "hiring-manager", "admin", "finance", "finance_manager", "product-manager", "product-owner", "cpo"]; 

const industrySectors = [
  "Information Technology (IT) / Software",
  "Healthcare & Pharmaceuticals",
  "Banking, Finance & Insurance (BFSI)",
  "Manufacturing & Production",
  "Retail & E-commerce",
  "Education & EdTech",
  "Real Estate & Construction",
  "Logistics, Transportation & Supply Chain",
  "Consulting & Professional Services",
  "Automobile & Aviation",
  "Telecommunications & Media",
  "Hospitality & Tourism",
  "Agriculture & Food Processing",
  "Energy, Oil & Utilities",
  "Other",
];

const getCompanyIdConfig = (countryName) => {
  const c = countryName?.toLowerCase();
  if (c === "india") return { label: "CIN", placeholder: "Enter Corporate Identification Number (CIN)", helper: "e.g. U72900KA2021PTC123456" };
  if (c === "united states") return { label: "EIN", placeholder: "Enter Employer Identification Number (EIN)", helper: "e.g. 12-3456789" };
  return { label: "Registration Number", placeholder: "Enter Company Registration Number", helper: "Official tax or business ID" };
};
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const Register = ({ topOffset = 72 }) => {
  const theme = useTheme();
  const navigate = useNavigate?.() ?? ((p) => (window.location.href = p));
  const turnstileRef = useRef(null); // 👈 NEW

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    mobile: "",
    role: "employee",
    password: "",
    confirmPassword: "",
    companyName: "",
    cin: "",
    industrySector: "",
    customIndustrySector: "",
    country: "India",
    currency: "INR",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [turnstileToken, setTurnstileToken] = useState(null); // 👈 NEW
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [countryOptions, setCountryOptions] = useState([{ name: "India", currency: "INR", symbol: "₹" }]);

  const currencyOptions = React.useMemo(() => {
    const unique = [];
    const seen = new Set();
    countryOptions.forEach(c => {
      if (c.currency && !seen.has(c.currency)) {
        seen.add(c.currency);
        unique.push({ code: c.currency, symbol: c.symbol });
      }
    });
    unique.sort((a, b) => a.code.localeCompare(b.code));
    return unique;
  }, [countryOptions]);

  React.useEffect(() => {
    axios.get(`${API_URL}/api/user/countries`)
      .then(res => {
        if (res.data.success && res.data.data) {
          setCountryOptions(res.data.data);
        }
      })
      .catch(err =>
      {
        //  console.error("Error fetching countries:", err)
  });
  }, []);

  // OTP States
  const [showOTPDialog, setShowOTPDialog] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [registrationId, setRegistrationId] = useState("");
  const [otpEmail, setOtpEmail] = useState("");
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);

  const handleChange = (e) => {
    let { name, value } = e.target;
    if (name === "mobile") {
      value = value.replace(/\D/g, "").slice(0, 15);
    }
    setForm((s) => ({ ...s, [name]: value }));
  };

  const validate = () => {
    const { fullName, email, password, confirmPassword, companyName, cin, role } = form;
    if (!fullName || !email || !password || !confirmPassword) {
      setMessage({ type: "error", text: "Please fill all fields." });
      return false;
    }

    const nameRegex = /^[a-zA-Z\s.'-]+$/;
    if (!nameRegex.test(fullName.trim())) {
      setMessage({ type: "error", text: "Full Name should contain only letters and spaces (no numbers or special characters)." });
      return false;
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email.trim())) {
      setMessage({ type: "error", text: "Please enter a valid email address (e.g., user@example.com)." });
      return false;
    }

    if (role === "admin") {
      if (!companyName || !companyName.trim()) {
        setMessage({ type: "error", text: "Please enter your Company Name." });
        return false;
      }
      if (!cin || !cin.trim()) {
        const idCfg = getCompanyIdConfig(form.country);
        setMessage({ type: "error", text: `Please enter your ${idCfg.label}.` });
        return false;
      }
      if (!form.industrySector || !form.industrySector.trim()) {
        setMessage({ type: "error", text: "Please select an Industry Sector." });
        return false;
      }
      if (form.industrySector === "Other" && (!form.customIndustrySector || !form.customIndustrySector.trim())) {
        setMessage({ type: "error", text: "Please specify your Industry Sector." });
        return false;
      }
      if (!form.country || !form.country.trim()) {
        setMessage({ type: "error", text: "Please select a Country." });
        return false;
      }
      if (!form.mobile || !form.mobile.trim()) {
        setMessage({ type: "error", text: "Please enter your Mobile / Phone Number." });
        return false;
      }
      const mobileDigitsRegex = /^\d{10,15}$/;
      if (!mobileDigitsRegex.test(form.mobile.trim())) {
        setMessage({ type: "error", text: "Mobile number must contain between 10 and 15 digits (numbers only, no letters or special characters)." });
        return false;
      }
    }

    if (role !== "admin") {
      const personalDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com', 'rediffmail.com', 'ymail.com', 'live.com'];
      const emailDomain = email.split('@')[1]?.toLowerCase();
      if (emailDomain && personalDomains.includes(emailDomain)) {
        setMessage({ type: "error", text: "Please use your official company email address for registration." });
        return false;
      }
    }

    if (password.length < 8) {
      setMessage({ type: "error", text: "Password must be at least 8 characters." });
      return false;
    }
    if (password !== confirmPassword) {
      setMessage({ type: "error", text: "Passwords do not match." });
      return false;
    }
    // 👇 NEW: Check if Turnstile is completed
    if (!turnstileToken) {
      setMessage({ type: "error", text: "Please complete the security verification." });
      return false;
    }
    if (!agreedToTerms) {
      setMessage({ type: "error", text: "Please agree to the Terms of Use and Privacy Policy to continue." });
      return false;
    }
    return true;
  };

  // 👇 NEW: Handle Turnstile success
  const handleTurnstileSuccess = (token) => {
    setTurnstileToken(token);
    setMessage({ type: "", text: "" }); // Clear any previous error
  };

  // 👇 NEW: Handle Turnstile expiration
  const handleTurnstileExpire = () => {
    setTurnstileToken(null);
    setMessage({ type: "error", text: "Security verification expired. Please complete it again." });
  };

  // 👇 NEW: Handle Turnstile error
  const handleTurnstileError = (error) => {
    //console.error("Turnstile error:", error);
    setTurnstileToken(null);
    setMessage({ type: "error", text: "Security verification failed. Please refresh and try again." });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });
    if (!validate()) return;

    try {
      setLoading(true);
      const finalIndustrySector =
        form.industrySector === "Other"
          ? form.customIndustrySector.trim()
          : form.industrySector;

      const payload = {
        firstName: form.fullName,
        email: form.email,
        mobile: form.mobile ? form.mobile.trim() : undefined,
        password: form.password,
        role: form.role,
        agreedToTerms: agreedToTerms,
        turnstileToken: turnstileToken, // 👈 NEW: Include token
        ...(form.role === "admin" && {
          companyName: form.companyName.trim(),
          cin: form.cin.trim().toUpperCase(),
          industrySector: finalIndustrySector,
          country: form.country.trim(),
          currency: form.currency,
          mobile: form.mobile ? form.mobile.trim() : undefined,
        }),
      };

      const response = await axios.post(`${API_URL}/api/user/register`, payload);

      // Reset Turnstile after successful submission
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
      setTurnstileToken(null);

      if (response.data.requiresOTP && response.data.registrationId) {
        setRegistrationId(response.data.registrationId);
        setOtpEmail(response.data.email);
        setShowOTPDialog(true);
        setOtp("");
        setOtpError("");
        setMessage({ type: "success", text: response.data.message });
      } else {
        setMessage({ type: "success", text: response.data?.message || "Registered successfully." });
        setTimeout(() => navigate("/login"), 1200);
      }
    } catch (err) {
      const serverError = err?.response?.data;
      let errorMsg = "Registration failed.";
      
      if (serverError?.errors) {
        errorMsg = serverError.errors[0].msg;
      } else if (serverError?.message) {
        errorMsg = serverError.message;
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      setMessage({ type: "error", text: errorMsg });
      //console.error("Registration error:", JSON.stringify(err.response?.data) || err.message);

      // Reset Turnstile on error
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
      setTurnstileToken(null);
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSubmit = async () => {
    setOtpError("");
    setIsVerifyingOTP(true);

    try {
      const response = await axios.post(`${API_URL}/api/user/verify-registration-otp`, {
        registrationId: registrationId,
        otp: otp,
      });

      setShowOTPDialog(false);
      setMessage({ type: "success", text: response.data.message || "Registration successful!" });
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      const errorMessage = err?.response?.data?.message || "OTP verification failed. Please try again.";
      
      if (errorMessage.includes("expired") || errorMessage.includes("Expired")) {
        setOtpError("OTP has expired. Please register again.");
        setTimeout(() => {
          setShowOTPDialog(false);
          setMessage({ type: "error", text: "OTP expired. Please register again." });
        }, 2000);
      } else if (errorMessage.includes("Invalid")) {
        setOtpError("Invalid OTP. Please check and try again.");
      } else {
        setOtpError(errorMessage);
      }
      //console.error(err);
    } finally {
      setIsVerifyingOTP(false);
    }
  };

  const handleOTPDialogClose = () => {
    setShowOTPDialog(false);
    setOtp("");
    setOtpError("");
    setRegistrationId("");
    setMessage({ type: "error", text: "Registration cancelled. Please try again." });
  };

  return (
    <Box sx={{ minHeight: "100vh", pt: 1, pb: 8 }}>
      <Container maxWidth="lg" sx={{ display: "flex", justifyContent: "center" }}>
        <Card
          elevation={8}
          sx={{
            width: "100%",
            maxWidth: 1200,
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 28px 60px rgba(12,30,60,0.08)"
          }}
        >
          <Grid container>
            <Grid item xs={12} md={6}>
              <CardContent sx={{ p: { xs: 3, md: 6 } }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                  <Avatar sx={{ bgcolor: BLUE.main, width: 48, height: 48 }}>
                    <CheckCircle />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>
                      Create your account
                    </Typography>
                  </Box>
                </Stack>

                <Divider sx={{ my: 2 }} />

                {message.text && (
                  <Alert severity={message.type === "error" ? "error" : "success"} sx={{ mb: 2 }}>
                    {message.text}
                  </Alert>
                )}

                <Box component="form" noValidate onSubmit={handleSubmit}>
                  <TextField
                    name="fullName"
                    label="Full name"
                    value={form.fullName}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    required
                    autoComplete="name"
                  />

                  <TextField
                    name="email"
                    label={form.role === "admin" ? "Email Address" : "Official Company Email"}
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    required
                    autoComplete="email"
                    helperText={form.role === "admin" ? "Personal or Company email address" : "Must be your official company email address (e.g., user@company.com)"}
                  />

                  <TextField
                    select
                    name="role"
                    label="Role"
                    value={form.role}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                  >
                    {roles.map((r) => {
                      let label = r.charAt(0).toUpperCase() + r.slice(1);
                      if (r === "hr") label = "HR";
                      else if (r === "hr-manager") label = "HR Manager";
                      else if (r === "hiring-manager") label = "Hiring Manager";
                      else if (r === "project-manager") label = "Project Manager";
                      else if (r === "product-manager") label = "Product Manager";
                      else if (r === "product-owner") label = "Product Owner";
                      else if (r === "cpo") label = "CPO / CTO / Executive";
                      else if (r === "finance_manager") label = "Finance Manager";
                      return (
                        <MenuItem key={r} value={r}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  {form.role === "admin" && (
                    <>
                      <TextField
                        name="companyName"
                        label="Company Name"
                        value={form.companyName}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        required
                        helperText="Official registered company name"
                      />

                      <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
                        <TextField
                          name="cin"
                          label={getCompanyIdConfig(form.country).label}
                          placeholder={getCompanyIdConfig(form.country).placeholder}
                          value={form.cin}
                          onChange={(e) => {
                            const val = e.target.value.toUpperCase();
                            setForm((s) => ({ ...s, cin: val }));
                          }}
                          fullWidth
                          margin="normal"
                          required
                          inputProps={{ maxLength: 35 }}
                          helperText={getCompanyIdConfig(form.country).helper}
                        />

                        <TextField
                          select
                          name="industrySector"
                          label="Industry Sector"
                          value={form.industrySector}
                          onChange={handleChange}
                          fullWidth
                          margin="normal"
                          required
                          helperText="Primary industry sector"
                        >
                          {industrySectors.map((sector) => (
                            <MenuItem key={sector} value={sector}>
                              {sector}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>

                      {form.industrySector === "Other" && (
                        <TextField
                          name="customIndustrySector"
                          label="Specify Industry Sector"
                          value={form.customIndustrySector}
                          onChange={handleChange}
                          fullWidth
                          margin="normal"
                          required
                          helperText="Enter your custom industry sector"
                          autoFocus
                        />
                      )}

                      <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
                        <TextField
                          select
                          name="country"
                          label="Country"
                          value={form.country}
                          onChange={(e) => {
                            const selectedCountry = countryOptions.find(c => c.name === e.target.value);
                            if (selectedCountry && selectedCountry.currency) {
                              setForm(s => ({ ...s, country: e.target.value, currency: selectedCountry.currency }));
                            } else {
                              handleChange(e);
                            }
                          }}
                          fullWidth
                          margin="normal"
                          required
                          helperText="Select company's country"
                        >
                          {countryOptions.map((c) => (
                            <MenuItem key={c.name} value={c.name}>
                              {c.name} {c.name !== "Other" ? `(${c.currency} - ${c.symbol})` : ""}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextField
                          select
                          name="currency"
                          label="Currency"
                          value={form.currency}
                          onChange={handleChange}
                          fullWidth
                          margin="normal"
                          required
                          helperText="Select company's base currency"
                        >
                          {currencyOptions.map((curr) => (
                            <MenuItem key={curr.code} value={curr.code}>
                              {curr.code} ({curr.symbol})
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>

                      <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>


                        <TextField
                          name="mobile"
                          label="Mobile / Phone Number"
                          value={form.mobile}
                          onChange={handleChange}
                          fullWidth
                          margin="normal"
                          required
                          helperText="Contact number for verification"
                        />
                      </Box>
                    </>
                  )}

                  <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
                    <TextField
                      name="password"
                      label="Password"
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={handleChange}
                      fullWidth
                      margin="normal"
                      required
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            onClick={() => setShowPassword((s) => !s)}
                            onMouseDown={(e) => e.preventDefault()}
                            edge="end"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        )
                      }}
                      helperText="Minimum 8 characters"
                    />

                    <TextField
                      name="confirmPassword"
                      label="Confirm password"
                      type={showConfirm ? "text" : "password"}
                      value={form.confirmPassword}
                      onChange={handleChange}
                      fullWidth
                      margin="normal"
                      required
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            onClick={() => setShowConfirm((s) => !s)}
                            onMouseDown={(e) => e.preventDefault()}
                            edge="end"
                            aria-label={showConfirm ? "Hide confirm password" : "Show confirm password"}
                          >
                            {showConfirm ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        )
                      }}
                    />
                  </Box>

                  {/* Turnstile Widget */}
                  {TURNSTILE_SITE_KEY && (
                    <Box sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'center' }}>
                      <Turnstile
                        ref={turnstileRef}
                        siteKey={TURNSTILE_SITE_KEY}
                        onSuccess={handleTurnstileSuccess}
                        onExpire={handleTurnstileExpire}
                        onError={handleTurnstileError}
                        options={{
                          theme: 'auto',
                          size: 'normal',
                        }}
                      />
                    </Box>
                  )}

                  {!TURNSTILE_SITE_KEY && (
                    <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                      Turnstile is not configured. Please add REACT_APP_TURNSTILE_SITE_KEY to your .env file.
                    </Alert>
                  )}

                  <Box sx={{ mt: 1.5, mb: 1, display: "flex", justifyContent: "flex-start" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={agreedToTerms}
                          onChange={(e) => setAgreedToTerms(e.target.checked)}
                          color="primary"
                          size="small"
                          sx={{
                            color: "#1976d2",
                            "&.Mui-checked": {
                              color: "#1976d2",
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontSize: "0.85rem", color: "text.secondary", userSelect: "none" }}>
                          {form.role === "admin" ? "I and my establishment agree to PulseHR's " : "I agree to PulseHR's "}
                          <MuiLink
                            component={Link}
                            to="/terms"
                            onClick={(e) => e.stopPropagation()}
                            sx={{ color: "#1976d2", fontWeight: 600, textDecoration: "underline" }}
                          >
                            Terms of Use
                          </MuiLink>{" "}
                          and{" "}
                          <MuiLink
                            component={Link}
                            to="/privacy"
                            onClick={(e) => e.stopPropagation()}
                            sx={{ color: "#1976d2", fontWeight: 600, textDecoration: "underline" }}
                          >
                            Privacy Policy
                          </MuiLink>
                        </Typography>
                      }
                    />
                  </Box>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 2 }}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loading || !turnstileToken || !agreedToTerms}
                      sx={{
                        py: 1.25,
                        fontWeight: 700,
                        color: "#fff",
                        backgroundColor: "#1976d2",
                        "&:hover": { backgroundImage: BLUE.gradient, transform: "translateY(-2px)" }
                      }}
                    >
                      {loading ? <CircularProgress size={20} color="inherit" /> : "Create account"}
                    </Button>

                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => navigate("/")}
                      sx={{
                        py: 1.25,
                        borderColor: "#1976d2",
                        color: "text.primary",
                        "&:hover": { backgroundColor: theme.palette.grey[50] }
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: "center" }}>
                    Already have an account?{" "}
                    <Button onClick={() => navigate("/login")} sx={{ textTransform: "none", color: "black", fontWeight: "bold" }}>
                      Sign in
                    </Button>
                  </Typography>
                </Box>
              </CardContent>
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              sx={{
                position: "relative",
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                justifyContent: "center",
                p: { md: 2, lg: 3 },
                overflow: "hidden"
              }}
            >
              <Box
                component="img"
                src={heroImg}
                alt="Pulse HR preview"
                loading="lazy"
                sx={{
                  width: "88%",
                  maxHeight: 520,
                  height: "auto",
                  objectFit: "contain",
                  display: "block"
                }}
              />
            </Grid>
          </Grid>
        </Card>
      </Container>

      {/* OTP Dialog */}
      <Dialog open={showOTPDialog} onClose={handleOTPDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: '#1976d2', width: 32, height: 32 }}>
              <CheckCircle sx={{ fontSize: 20, color: 'white' }} />
            </Avatar>
            Verify OTP for Admin Registration
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
            OTP has been sent to {otpEmail || 'your email'}. Please enter the 6-digit code to complete registration.
          </Typography>
       
          {otpError && (
            <Alert severity="error" sx={{ mb: 2 }} icon={<ErrorOutline fontSize="small" />}>
              {otpError}
            </Alert>
          )}

          <TextField
            label="OTP Code"
            variant="outlined"
            fullWidth
            value={otp}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              setOtp(value.slice(0, 6));
            }}
            inputProps={{ maxLength: 6, inputMode: 'numeric' }}
            error={!!otpError}
            size="medium"
            autoFocus
            helperText="Enter 6-digit OTP"
            sx={{ mt: 1 }}
          />

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button 
              variant="text" 
              color="primary" 
              onClick={() => {
                setOtpError("");
                setMessage({ type: "info", text: "Please register again to receive a new OTP." });
                handleOTPDialogClose();
              }}
              sx={{ textTransform: 'none' }}
            >
              Didn't receive OTP? Register Again
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={handleOTPDialogClose} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleOTPSubmit} 
            variant="contained" 
            disabled={!otp || otp.length !== 6 || isVerifyingOTP}
            sx={{ 
              bgcolor: '#1976d2',
              '&:hover': { bgcolor: '#1565c0' }
            }}
          >
            {isVerifyingOTP ? <CircularProgress size={24} color="inherit" /> : "Verify & Register"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Register;